/* eslint-disable react/require-default-props -- defaults via destructuring; no defaultProps (React 19) */
import React from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import { FlexColumn, FlexRow } from '../GridsAndLayouts/GridsAndLayouts';
import Typography from '../Typography';
import './Tabs.scss';

const resolveVariant = (variant) => {
  if (variant === 'pills' || variant === 'pills-gray') return 'pill';
  return variant;
};

const Tabs = ({
  items = [],
  value = '',
  onChange,
  variant = 'underline',
  fullWidth = false,
  className = '',
  ariaLabel,
  idPrefix,
}) => {
  const kind = resolveVariant(variant);
  const showIndicator = kind === 'underline' || kind === 'segmented';

  const containerClasses = ['ds-tabs', `ds-tabs--${kind}`, fullWidth && 'ds-tabs--full', className]
    .filter(Boolean)
    .join(' ');

  /* ARIA tabs: roving tabindex + arrow/Home/End (APG); preventDefault avoids scroll / browser shortcuts */
  const handleKeyDown = (e, index) => {
    const count = items.length;
    if (count === 0) return;
    const navKeys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!navKeys.includes(e.key) || e.altKey || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    if (e.key === 'ArrowRight') onChange?.(items[(index + 1) % count].value);
    if (e.key === 'ArrowLeft') onChange?.(items[(index - 1 + count) % count].value);
    if (e.key === 'Home') onChange?.(items[0].value);
    if (e.key === 'End') onChange?.(items[count - 1].value);
  };

  return (
    <FlexRow id={idPrefix} className={containerClasses} role="tablist" aria-label={ariaLabel} wrap={false}>
      {items.map((item, index) => {
        const isActive = item.value === value;
        const tabClasses = ['ds-tab', isActive && 'ds-tab--active'].filter(Boolean).join(' ');
        const tabDomId = idPrefix ? `${idPrefix}-tab-${index}` : `tab-${item.value}`;
        const panelDomId = idPrefix ? `${idPrefix}-panel-${index}` : `panel-${item.value}`;

        return (
          <Button
            key={item.value}
            variant="tertiary"
            size="base"
            role="tab"
            aria-selected={isActive}
            aria-controls={panelDomId}
            id={tabDomId}
            className={tabClasses}
            onClick={() => onChange?.(item.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            tabIndex={isActive ? 0 : -1}
          >
            <FlexRow className="ds-tab__row" align="center" justify="center">
              {item.icon ? (
                <FlexRow inline align="center" justify="center" className="ds-tab__icon" fullWidth={false}>
                  {item.icon}
                </FlexRow>
              ) : null}
              <Typography component="span" variant="bodyLarge" className="ds-tab__label">
                {item.label}
              </Typography>
              {item.badge !== undefined && item.badge !== null ? (
                <FlexColumn inline align="center" justify="center" className="ds-tab__badge" fullWidth={false}>
                  <Typography component="span" variant="bodySmall" className="ds-tab__badge-text">
                    {item.badge}
                  </Typography>
                </FlexColumn>
              ) : null}
            </FlexRow>
            {showIndicator ? (
              <FlexColumn
                className={['ds-tab__indicator', isActive && 'ds-tab__indicator--active'].filter(Boolean).join(' ')}
                aria-hidden
                fullWidth
              />
            ) : null}
          </Button>
        );
      })}
    </FlexRow>
  );
};

Tabs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.string.isRequired,
      icon: PropTypes.node,
      badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  value: PropTypes.string,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(['underline', 'pill', 'segmented', 'pills', 'pills-gray']),
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  /** Accessible name for the tab row when no visible heading labels it (WCAG). */
  ariaLabel: PropTypes.string,
  idPrefix: PropTypes.string,
};

export default Tabs;
